import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/PrintOutlined";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

import useSacramentalRecordData from "layouts/tables/data/sacramentalRecordData";

const TYPE_LABELS = {
  baptism: "Baptism",
  confirmation: "Confirmation",
  marriage: "Marriage",
  funeral: "Funeral",
};

function MemberPicker({ label, search, setSearch, results, onSelect }) {
  return (
    <Box sx={{ position: "relative" }}>
      <TextField
        label={label}
        placeholder="Search by name or phone..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        margin="normal"
      />
      {results.length > 0 && (
        <Card
          sx={{
            position: "absolute",
            zIndex: 10,
            width: "100%",
            mt: -1,
            maxHeight: 220,
            overflowY: "auto",
          }}
        >
          {results.map((m) => (
            <Box
              key={m.id}
              onClick={() => onSelect(m)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>{m.first_name?.[0]}</Avatar>
              <Typography variant="body2">
                {m.first_name} {m.surname}
              </Typography>
            </Box>
          ))}
        </Card>
      )}
    </Box>
  );
}
MemberPicker.propTypes = {
  label: PropTypes.string.isRequired,
  search: PropTypes.string.isRequired,
  setSearch: PropTypes.func.isRequired,
  results: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
};

function SacramentalRecords() {
  const {
    columns,
    rows,
    isLoading,
    error,
    typeFilter,
    setTypeFilter,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    certificateModalOpen,
    setCertificateModalOpen,
    selectedRecord,
    form,
    setForm,
    handleAdd,
    handleSubmit,
    handleDeleteConfirm,
    isSaving,
    primarySearch,
    setPrimarySearch,
    primaryResults,
    handleSelectPrimary,
    spouseSearch,
    setSpouseSearch,
    spouseResults,
    handleSelectSpouse,
    handlePrintCertificate,
  } = useSacramentalRecordData();

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading sacramental records...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  const cert = selectedRecord?.originalData;

  return (
    <Box>
      <PageHeader
        title="Sacramental Records"
        subtitle="Baptism, confirmation, marriage, and funeral registry."
        actions={
          <>
            <TextField
              select
              size="small"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              sx={{ minWidth: 160 }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">All types</MenuItem>
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  {v}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
              New Record
            </Button>
          </>
        }
      />

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
          showTotalEntries
          canSearch
          searchLabel="Search records..."
        />
      </Card>

      {/* Create / Edit */}
      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={form.id ? "Edit Record" : "New Sacramental Record"}
        onSave={handleSubmit}
        saveLabel={isSaving ? "Saving..." : form.id ? "Save Changes" : "Create Record"}
        saveDisabled={!form.primary_member_id || !form.record_date || isSaving}
        loading={isSaving}
        maxWidth="sm"
      >
        <FormSection>
          <TextField
            select
            label="Type"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            fullWidth
            margin="normal"
          >
            {Object.entries(TYPE_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v}
              </MenuItem>
            ))}
          </TextField>

          <MemberPicker
            label={form.type === "marriage" ? "Spouse 1" : "Member"}
            search={primarySearch}
            setSearch={(v) => {
              setPrimarySearch(v);
              setForm((prev) => ({ ...prev, primary_member_id: null }));
            }}
            results={primaryResults}
            onSelect={handleSelectPrimary}
          />

          {form.type === "marriage" && (
            <>
              <MemberPicker
                label="Spouse 2 (if a member)"
                search={spouseSearch}
                setSearch={(v) => {
                  setSpouseSearch(v);
                  setForm((prev) => ({ ...prev, spouse_member_id: null }));
                }}
                results={spouseResults}
                onSelect={handleSelectSpouse}
              />
              {!form.spouse_member_id && (
                <TextField
                  label="Or spouse name (non-member)"
                  value={form.spouse_name}
                  onChange={(e) => setForm({ ...form, spouse_name: e.target.value })}
                  fullWidth
                  margin="normal"
                />
              )}
            </>
          )}
        </FormSection>

        <FormSection>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Date"
              type="date"
              value={form.record_date}
              onChange={(e) => setForm({ ...form, record_date: e.target.value })}
              fullWidth
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Officiant"
              value={form.officiant_name}
              onChange={(e) => setForm({ ...form, officiant_name: e.target.value })}
              fullWidth
              margin="normal"
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Certificate Number"
              value={form.certificate_number}
              onChange={(e) => setForm({ ...form, certificate_number: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Location"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              fullWidth
              margin="normal"
            />
          </Box>
          <TextField
            label="Notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
        </FormSection>
      </FormDialog>

      {/* Certificate */}
      <Dialog
        open={certificateModalOpen}
        onClose={() => setCertificateModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          {cert && (
            <Box
              sx={{
                border: "4px double",
                borderColor: "primary.main",
                p: 4,
                textAlign: "center",
              }}
            >
              <Typography variant="overline" color="text.secondary">
                Certificate of {TYPE_LABELS[cert.type]}
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mt: 1, mb: 2 }}>
                {cert.primary_member
                  ? `${cert.primary_member.first_name} ${cert.primary_member.surname}`
                  : ""}
                {cert.type === "marriage" &&
                  ` & ${
                    cert.spouse_member
                      ? `${cert.spouse_member.first_name} ${cert.spouse_member.surname}`
                      : cert.spouse_name || ""
                  }`}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body1">
                on{" "}
                {new Date(cert.record_date).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
              {cert.location && (
                <Typography variant="body2" color="text.secondary">
                  at {cert.location}
                </Typography>
              )}
              {cert.officiant_name && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Officiated by {cert.officiant_name}
                </Typography>
              )}
              {cert.certificate_number && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 3 }}
                >
                  Certificate No. {cert.certificate_number}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCertificateModalOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrintCertificate}>
            Print
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message="Are you sure you want to delete this record?"
        confirmLabel="Delete"
      />
    </Box>
  );
}

export default SacramentalRecords;
